export interface ExpenseCommentHistory {
  status: ExpenseStatus;
  comment?: string | null;
  author?: string | null;
  at?: string | Date | number;
}

/** Message du fil de discussion d'une ligne de frais (RH ↔ consultant). */
export interface ExpenseComment {
  /** 'admin' = service RH ; tout le reste = le consultant. */
  author: string;
  authorName?: string;
  text: string;
  at?: string;
}

export interface ExpensesByMonth {
  id: number;
  userId: number;
  year: number;
  month: number;
  expensesStatus: ExpenseStatus;
  expenses: Expense[];
  /** Présent sur les enveloppes renvoyées par `/expenses/:year/:month/by-mission`. */
  missionId?: string | null;
}

export interface Expense {
  id?: number | string;
  date: string;
  type: string;
  description: string;
  /** Montant remboursé — calculé par le BE. `null` tant qu'il n'est pas encore posé (IK). */
  amount: number;
  status: ExpenseStatus;
  receiptUrl?: string;
  needsReceipt?: boolean;
  reviewerComment?: string;
  lastComment?: string;
  statusHistory?: ExpenseCommentHistory[];
  /** Fil de discussion RH ↔ consultant de la ligne (source du modal message). */
  comments?: ExpenseComment[];
  /** Drapeau BE : la ligne attend une action du consultant (miroir de `status = info_required`). */
  infoRequired?: boolean;

  /**
   * **Lecture automatique du justificatif** (suivora-api#1778).
   *
   * `null` tant qu'aucune n'a été demandée — une ligne sans justificatif, ou déposée avant que la
   * lecture n'existe. `SKIPPED` dit qu'il n'y avait **rien à faire** : tout était déjà saisi.
   *
   * ⚠️ Distinguer les deux est l'objet du champ : sans lui, « pas encore lu » et « lu, et il n'y
   * avait rien à en tirer » sont la même absence de valeurs.
   */
  ocrStatus?: string | null;
  ocrAt?: string | null;
  /**
   * Ce que la machine a lu, **avec sa confiance par champ** — gardé même quand la valeur n'a pas
   * été reprise, parce que la personne l'avait déjà saisie.
   *
   * ⚠️ `appliedFields` vit **dans** cet objet, pas à côté. Et aucun seuil n'est appliqué côté
   * serveur : la confiance est servie, l'écran décide (`features/expenses/lectureDuJustificatif`).
   *
   * `unknown` : la forme est lue par un module qui la valide, jamais désassemblée à la volée.
   */
  ocrFields?: unknown;

  // ===== Champs Indemnités Kilométriques (IK) — présents seulement pour type mileage =====
  /** Distance aller simple (km) ; doublée au calcul si `roundTrip`. */
  distanceKm?: number;
  /** Trajet aller-retour (true par défaut sur une IK). */
  roundTrip?: boolean;
  /** Libellé départ (« Domicile »). */
  fromLabel?: string;
  /** Libellé destination (« Renault · Guyancourt »). */
  toLabel?: string;
  /** Surcharge ponctuelle de la puissance fiscale (sinon défaut profil). */
  vehicleFiscalPower?: number;
  /** Véhicule 100 % électrique → IK majorées +20 % (résolu BE : ligne sinon défaut profil). */
  vehicleIsElectric?: boolean;
  /** Mission rattachée (mois multi-mission). */
  missionId?: string | null;
}

/**
 * « Information requise » : le RH a demandé une correction sur une ligne soumise, sans la
 * refuser → cette ligne se rouvre à l'édition + resoumission. Statut réel BE `info_required`
 * (+ drapeau `infoRequired`). Repli sur l'ancienne dérivation `PENDING + reviewerComment` par
 * sécurité tant que toutes les données ne portent pas le nouveau statut.
 */
export const isInfoRequiredExpense = (e: Expense): boolean =>
  e.status === ExpenseStatus.INFO_REQUIRED ||
  e.infoRequired === true ||
  (e.status === ExpenseStatus.PENDING && !!e.reviewerComment?.trim());

/**
 * Estimation de distance domicile → client renvoyée par `GET /me/mileage/estimate`.
 * `distanceKm` est l'aller simple ; `null` + `reason` quand une adresse manque.
 */
export interface MileageEstimate {
  distanceKm: number | null;
  roundTripKm?: number | null;
  clientName?: string | null;
  clientCity?: string | null;
  fromLabel?: string | null;
  toLabel?: string | null;
  reason?: string | null;
}

export interface ExpenseData {
  date: string;
  type: string;
  paymentMethod: PaymentMethod;
  description: string;
  vatRate: number;
  periodicity: RecurrenceType;
  amountTax: number;
  distance?: number;
  status: ExpenseStatus;
  startAddress?: string;
  endAddress?: string;
  amountInclTax: number;
  amountExclTax: number;
  proofOfExpense: string;
  receiptUrl: string;
}

export interface ExpenseOption {
  id?: string;
  label: string;
  value: string;
  type?: ExpenseType;
}

/**
 * Type de NDF autorisé, renvoyé par `GET /expenses/types` (remplace la liste en
 * dur). Jeu restreint pour les internes, complet (dont IK) pour les consultants.
 * `value` = ce qui est stocké dans `Expense.type`.
 */
/**
 * Droit restant sur un type de frais contingenté (suivora-api#851).
 *
 * Certains frais ne se déclarent qu'un nombre de fois par période : un abonnement annuel une fois
 * par an, un achat d'ordinateur une fois par douze mois glissants, une invitation au restaurant
 * cinq fois par mois. Le décompte appartient à l'API : elle seule voit les lignes, et elle seule
 * peut refuser la seconde — masquer le type côté web ne serait pas une règle, l'application mobile
 * l'ignorerait.
 *
 * **Ce qui consomme le droit : tout sauf un refus.** Une ligne refusée ne coûte pas au consultant
 * sa déclaration de l'année, sinon une décision de gestion deviendrait une sanction. Mais une ligne
 * *en attente* réserve le droit — sans quoi cinq déclarations annuelles identiques passeraient tant
 * que la première n'est pas traitée, et le RH en recevrait cinq à refuser.
 *
 * **La période s'ancre sur la date de la dépense**, jamais sur celle de la saisie : déclarer en
 * janvier une dépense de décembre ne consomme pas le droit de l'année neuve.
 */
export interface ExpenseTypeQuota {
  /** Déclarations autorisées sur la période. */
  max: number;
  /** `YEAR` compte sur l'année civile, `ROLLING_12M` sur les douze mois précédents. */
  period: 'DAY' | 'MONTH' | 'ROLLING_12M' | 'YEAR';
  /**
   * Déclarations déjà posées sur la période courante.
   *
   * Servi en plus de `remaining` pour qu'on écrive « 3 sur 5 » sans soustraire : un décompte
   * recalculé côté écran finit par diverger de celui qui refuse la ligne.
   */
  used: number;
  /** Ce qu'il reste au compte courant. `0` ⇒ le type ne peut plus être choisi. Jamais négatif. */
  remaining: number;
  /** Date de la déclaration qui consomme le droit, pour pouvoir la nommer. ISO 8601. */
  lastUsedAt?: string | null;
}

export interface ExpenseTypeOption {
  id: string;
  value: string;
  label: string;
  /** Aide affichée au consultant (tooltip/sous le sélecteur). `null` si absente. */
  description?: string | null;
  isMileage: boolean;
  requiresReceipt: boolean;
  /**
   * Contingent du type pour le compte courant, ou absent quand le type n'est pas limité — le cas
   * de la très grande majorité d'entre eux. Absent ⇒ rien n'est restreint.
   */
  quota?: ExpenseTypeQuota | null;
}

export interface PaymentOption {
  id?: string;
  label: string;
  value: string;
  type?: PaymentMethod;
  /** Suffixe affiché en badge (ex. « Remboursable »). */
  suffix?: string;
}

export interface VATRateOption {
  label: string;
  value: number;
}

export interface ExpenseRecurrenceOption {
  id?: string;
  label: string;
  value: string;
  type?: RecurrenceType;
}

export enum RecurrenceType {
  SINGLE = 'single',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
  EVERY_BUSINESS_DAY_OF_THE_MONTH = 'every_business_day_of_the_month',
}

export enum ExpenseType {
  FOOD = 'food',
  TRAVEL = 'travel',
  TRANSPORTATION = 'transportation',
  SUPPLIES = 'supplies',
  OTHER_EXPENSES = 'otherExpenses',
  LODGING = 'lodging',
  REMOTE_WORK = 'remoteWork',
  IT_EQUIPMENT = 'itEquipment',
  ANNUAL_IT_EQUIPMENT = 'annualItEquipment',
  TRAINING = 'training',
}

export enum ExpensesStatus {
  OPEN = 'open',
  CREATED = 'created',
  VALIDATED = 'validated',
  PENDING = 'pending',
  REFUSED = 'refused',
}

export enum ExpenseStatus {
  OPEN = 'open',
  CREATED = 'created',
  DRAFT = 'draft',
  VALIDATED = 'validated',
  PENDING = 'pending',
  /** Ligne soumise renvoyée au consultant pour complément (message RH, sans refus) — BE #252. */
  INFO_REQUIRED = 'info_required',
  /** Mois partiellement revu : ≥1 ligne revue, il reste des lignes en attente (BE #133). */
  IN_PROGRESS = 'in_progress',
  /** Mois entièrement revu avec un mélange validées/refusées (BE #133). */
  PARTIALLY_VALIDATED = 'partially_validated',
  REFUSED = 'refused',
}

export enum FileType {
  IMAGE = 'image',
  PDF = 'pdf',
}

export enum PaymentMethod {
  CASH = 'cash',
  CHECK = 'check',
  CREDIT_CARD = 'credit_card',
  COMPANY_CARD = 'company_card',
  BANK_TRANSFER = 'bank_transfer',
}
